import React, { useState, useEffect } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SelectChargeType from "../ui/SelectChargeType";
import { useNotification } from "../../../../context/NotificationContext";

const chargeTypes = [
  { value: "Extra Bed", label: "Extra Bed" },
  { value: "Laundry Services", label: "Laundry Services" },
  { value: "Room Service", label: "Room Service" },
  { value: "Mini Bar", label: "Mini Bar" },
  { value: "Airport Pickup", label: "Airport Pickup" },
  { value: "Early Check-in", label: "Early Check-in" },
  { value: "Late Checkout", label: "Late Checkout" },
  { value: "Pet Fee", label: "Pet Fee" },
  { value: "Parking", label: "Parking" },
  { value: "Wi-Fi Upgrade", label: "Wi-Fi Upgrade" },
  { value: "Spa Services", label: "Spa Services" },
  { value: "Miscellaneous", label: "Miscellaneous" },
];

// Default tax option for "None"
const noneTaxOption = { value: "none", label: "None", taxRate: 0 };

// Date format constant
//const DATE_FORMAT = "DD MMMM YYYY"; // e.g., "26th June 2026"

// Get business_date from localStorage, fallback to system date, adjusted to default timezone
const getBusinessDate = () => {
  const storedBusinessDate = localStorage.getItem("business_date");
  const defaultTimezone = "Asia/Kolkata";
  return storedBusinessDate 
    ? moment.tz(storedBusinessDate, defaultTimezone).toDate()
    : moment.tz(defaultTimezone).toDate();
};

const AddChargePopup = ({ onSubmit, bookingId, onCancel, isVisible, initialData }) => {
  const [formValues, setFormValues] = useState({
    chargeType: null,
    amount: "",
    remarks: "",
    taxRate: noneTaxOption,
    chargeDate: getBusinessDate(),
  });

  const [taxRates, setTaxRates] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const { notify } = useNotification();

  // Pre-fill form values when in edit mode (initialData is provided)
  useEffect(() => {
    if (initialData) {
      // Extract tax rate from tax_breakdown if available
      let selectedTaxRate = noneTaxOption;
      if (initialData.tax_breakdown && initialData.tax_breakdown.length > 0) {
        const taxBreakdown = initialData.tax_breakdown[0];
        selectedTaxRate = taxRates.find(tax => 
          tax.value === taxBreakdown.taxName && tax.taxRate === taxBreakdown.taxRate
        ) || noneTaxOption;
      }

      // Find the charge type object from chargeTypes based on initialData.chargeType
      const initialChargeType = chargeTypes.find(type => type.value === initialData.chargeType) || null;

      setFormValues({
        chargeType: initialChargeType, // Ensure chargeType is an object with value and label
        amount: initialData.amount || "",
        remarks: initialData.referenceType || initialChargeType?.label || "", // Use referenceType if available, else chargeType label
        taxRate: selectedTaxRate,
        chargeDate: initialData.chargeDate ? new Date(initialData.chargeDate) : getBusinessDate(),
      });
    }
  }, [initialData, taxRates]);

  // Fetch tax rates when the component mounts
  useEffect(() => {
    const fetchTaxRates = async () => {
      try {
        const response = await axios.get(`${apiUrl}/hmstax/listing`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId: localStorage.getItem("companyId") },
        });

        if (response.data.success) {
          const fetchedTaxRates = response.data.taxes.map(tax => ({
            value: tax.tax_name,
            label: `${tax.tax_name} (${parseFloat(tax.tax_rate)}%)`,
            taxRate: parseFloat(tax.tax_rate),
          }));
          setTaxRates([noneTaxOption, ...fetchedTaxRates]);
        } else {
          console.error("Failed to fetch tax rates:", response.data.message);
          setTaxRates([noneTaxOption]);
        }
      } catch (err) {
        console.error("Error fetching tax rates:", err);
        setTaxRates([noneTaxOption]);
      }
    };

    fetchTaxRates();
  }, [apiUrl, token]);

  // Update formValues when business_date changes in localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const newBusinessDate = getBusinessDate();
      setFormValues((prev) => ({
        ...prev,
        chargeDate: newBusinessDate,
      }));
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (field) => (selectedOption) => {

    setFormValues((prev) => ({
      ...prev,
      [field]: selectedOption,
      ...(field === "chargeType" && { remarks: selectedOption ? selectedOption.name : "" }),
    }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleDateChange = (date) => {
    setFormValues((prev) => ({
      ...prev,
      chargeDate: date,
    }));
    setErrors((prev) => ({ ...prev, chargeDate: "" }));
  };

  const validateForm = () => {
    let validationErrors = {};
    
    if (!formValues.chargeType || !formValues.chargeType.value) {
      validationErrors.chargeType = "Charge Type is required";
    }
    if (!formValues.amount || isNaN(parseFloat(formValues.amount)) || parseFloat(formValues.amount) <= 0) {
      validationErrors.amount = "Please enter a valid amount";
    }
    if (!formValues.chargeDate || isNaN(formValues.chargeDate.getTime())) {
      validationErrors.chargeDate = "Please enter a valid charge date";
    }
    
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const calculateTaxAmount = () => {
    const chargeAmount = parseFloat(formValues.amount) || 0;
    const taxRate = formValues.taxRate?.taxRate || 0;
    return (chargeAmount * (taxRate / 100)).toFixed(2);
  };

  const calculateTotal = () => {
    const chargeAmount = parseFloat(formValues.amount) || 0;
    const taxAmount = parseFloat(calculateTaxAmount()) || 0;
    return (chargeAmount + taxAmount).toFixed(2);
  };

  const generateTaxBreakdown = () => {
    const taxRate = formValues.taxRate;
    if (!taxRate || taxRate.value === "none") {
      return [];
    }
    const taxAmount = parseFloat(calculateTaxAmount()) || 0;
    return [
      {
        taxName: taxRate.value,
        taxRate: taxRate.taxRate,
        taxAmount: taxAmount,
      },
    ];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      notify("error","Please fix the validation errors");
      return;
    }

    if (hasSubmitted) {
      console.log("Form already submitted, ignoring duplicate submission");
      return;
    }

    setHasSubmitted(true);
    setLoading(true);

    try {
      const chargeAmount = parseFloat(formValues.amount);
      const taxAmount = parseFloat(calculateTaxAmount()) || 0;
      const totalAmount = chargeAmount + taxAmount;
      const taxBreakdown = generateTaxBreakdown();
  
      const defaultTimezone = "Asia/Kolkata";
      const chargeDateUTC = moment.tz(formValues.chargeDate, defaultTimezone).startOf('day').toISOString();
  
      const payload = {
        bookingId: bookingId,
        chargeType: formValues.chargeType?.value || "",
        amount: chargeAmount,
        taxAmount: taxAmount || 0,
        taxBreakdown: taxBreakdown,
        referenceId: formValues.chargeType?.value || "",
        referenceType: formValues.remarks || null,
        description: formValues.chargeType?.name || "", // Should reflect the selected charge type label
        chargeDate: chargeDateUTC,
      };

      console.log("Submitting payload:", payload);
      console.log("formValues.chargeType at submission:", formValues.chargeType);

      if (initialData?.chargeId) {
        payload.chargeId = initialData.chargeId;
      }

      if (!payload.chargeType) {
        throw new Error("Charge Type is missing in the payload");
      }

      const response = await axios.post(
        `${apiUrl}/transactions/add-additional-charges`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      if (onSubmit) onSubmit({ ...payload, totalAmount, charge_id: response.data.charge.charge_id });
      onCancel();
    } catch (error) {
      notify("error","Failed to save charge: ${error.response?.data?.message || error.message || 'Unknown error'}");
      console.error("Error saving charge:", error);
    } finally {
      setLoading(false);
      setHasSubmitted(false);
    }
  };

  return (
    <div 
      className={`modal custom-modal ${isVisible ? "show" : ""}`}
      tabIndex="-1" 
      role="dialog"
      style={{ 
        display: isVisible ? "block" : "none",
        position: "fixed", 
        top: 0, 
        right: 0, 
        height: "100vh",
        width: "40%",
        zIndex: 1050,
      }}
    >
      <div 
        className="modal-dialog modal-lg"
        role="document"
        style={{
          margin: 0,
          position: "fixed",
          right: 0,
          height: "100vh",
          width: "40%",
        }}
      >
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div className="modal-content" style={{ height: "100%", border: "none", borderRadius: 0 }}>
            <div className="modal-header">
              <h5 className="modal-title">{initialData?.chargeId ? "Edit Charge" : "Add Charge"}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onCancel}
                aria-label="Close"
                style={{ 
                  background: "transparent", 
                  border: "none", 
                  fontSize: "1.5rem", 
                  cursor: "pointer" 
                }}
              />
            </div>

            <div className="modal-body" style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}>
              <div className="mb-3">
                <label className="form-label">Charge Type</label>
                <SelectChargeType
                  onSelectChargeType={handleSelectChange("chargeType")}
                  selectedValue={formValues.chargeType}
                  isDisabled={false}
                />
                {errors.chargeType && <small className="text-danger">{errors.chargeType}</small>}
              </div>

              <div className="mb-3">
                <label className="form-label">Amount</label>
                <input 
                  type="number" 
                  name="amount" 
                  value={formValues.amount} 
                  onChange={handleInputChange} 
                  className="form-control" 
                  placeholder="Enter amount"
                />
                {errors.amount && <small className="text-danger">{errors.amount}</small>}
              </div>

              <div className="mb-3">
                <label className="form-label">Tax Rate (Optional)</label>
                <Select 
                  options={taxRates} 
                  onChange={handleSelectChange("taxRate")}
                  value={formValues.taxRate}
                  isSearchable={false}
                />
                {formValues.taxRate?.value !== "none" && (
                  <small className="text-muted">
                    Tax Amount: ₹{calculateTaxAmount()}
                  </small>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Charge Date</label>
                <DatePicker
                  selected={formValues.chargeDate}
                  onChange={handleDateChange}
                  className="form-control"
                  placeholderText="Select charge date"
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={15}
                />
                {errors.chargeDate && <small className="text-danger">{errors.chargeDate}</small>}
              </div>

              <div className="mb-3">
                <label className="form-label">Remarks</label>
                <textarea 
                  name="remarks" 
                  value={formValues.remarks} 
                  onChange={handleInputChange} 
                  className="form-control" 
                  placeholder="Enter remarks (optional)"
                  rows="3"
                />
              </div>

              <div className="mt-3">
                <label className="form-label fw-bold text-success" style={{ fontSize: "1.1rem" }}>
                  Total Amount: ₹{calculateTotal()}
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button type="submit" className="btn btn-primary" disabled={loading || hasSubmitted}>
                {loading ? "Saving..." : initialData?.chargeId ? "Update" : "Save"}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddChargePopup;